<script lang="ts" context="module">
    export const NO_OPTIONS_WARNING =
        'Rendering DropdownMenu failed, no options provided';
</script>

<script lang="ts">
    import Chevron from '@amp/web-app-components/assets/icons/chevron.svg';
    import clickOutside from '@amp/web-app-components/src/actions/click-outside';
    import type { Option } from '@amp/web-app-components/src/types';
    import { onMount } from 'svelte';

    export let translateFn: (
        str: string,
        values?: Record<string, string | number>,
    ) => string;
    export let options: Option[];
    export let position: 'up' | 'down';

    let show = false; // dropdown state
    let button = null; // button DOM reference

    let currentItemIndex = 0; // default to the first item
    $: currentItem = options[currentItemIndex];

    const handleDropdownItemClick = (index: number) => {
        currentItemIndex = index;
        show = false;
    };

    const handleContinueButtonClick = (e: Event) => {
        currentItem.callback(e);
    };

    const handleClickOutside = (event: Event) => {
        // use composedPath() to take into account shadow DOM use case
        const eventTarget = event.composedPath()[0];

        if (show && !button.contains(eventTarget)) {
            show = false;
        }
    };

    const handleEscape = (event: KeyboardEvent) => {
        if (show && event.key === 'Escape') {
            show = false;
        }
    };

    onMount(() => {
        if (!options.length) {
            // TODO: rdar://89627471 (Shared components: unable to import logger into shared/components)
            console.warn(NO_OPTIONS_WARNING);
        }
    });
</script>

{#if options.length}
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
        class="select-container"
        data-testid="select-container"
        class:open-up={show && position === 'up'}
        class:open-down={show && position === 'down'}
        on:keyup={handleEscape}
    >
        <button
            on:click={() => (show = !show)}
            bind:this={button}
            class="dropdown-button"
            data-testid="dropdown-button"
            aria-haspopup="menu"
            aria-controls="select-list"
            aria-expanded={show}
        >
            <span id="button-label">{currentItem.name}</span>
            <Chevron class="chevron" aria-hidden="true" />
        </button>

        {#if show}
            <ul
                use:clickOutside={handleClickOutside}
                id="select-list"
                data-testid="select-list"
                role="menu"
                class="select-list"
            >
                {#each options as option, i (option.name)}
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <!-- svelte-ignore a11y-role-supports-aria-props -->
                    <li
                        data-testid={`list-item-${i}`}
                        role="menuitem"
                        tabindex="0"
                        aria-selected={option === currentItem}
                        class:selected={option === currentItem}
                        class="dropdown-select-item"
                        on:click={() => handleDropdownItemClick(i)}
                    >
                        <span>{option.name}</span>
                        <!-- add chevron component in the last li if position is up, or the first li if position is down -->
                        {#if (i === options.length - 1 && position === 'up') || (i === 0 && position === 'down')}
                            <Chevron class="chevron" aria-hidden="true" />
                        {/if}
                    </li>
                {/each}
            </ul>
        {/if}

        <button
            on:click={(e) => handleContinueButtonClick(e)}
            class="select-button"
            data-testid="select-button"
        >
            {translateFn('AMP.Shared.DropdownMenu.Continue')}
        </button>
    </div>
{/if}

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'amp/stylekit/core/mixins/line-clamp' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;

    $dropdown-menu-border-radius: var(
        --global-border-radius-medium,
        #{$global-border-radius-medium}
    );
    $dropdown-light-gray: rgb(112, 112, 112);
    $dropdown-background: rgb(87, 87, 87);
    $dropdown-light-gray-icon: rgb(153, 153, 153);

    .select-container {
        --selectHeight: var(--overrideSelectHeight, 32px);
        width: var(--overridSelectContainerWidth, auto);
        position: relative;
        display: grid;
        grid-template-columns: 1fr auto;
        grid-gap: 22px;
        align-items: center;
        grid-template-areas: 'away-select away-button';
        z-index: var(--z-default);
    }

    .dropdown-button {
        width: 160px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-radius: $dropdown-menu-border-radius;
        border: 1px solid var(--dropdownLightGray, $dropdown-light-gray);
        background-color: var(--dropdownBackground, $dropdown-background);
        min-height: var(--selectHeight);
        user-select: none;
        color: var(--systemPrimary-onDark);
        grid-area: away-select;
        padding-inline-start: 30px;
        font: var(--title-3);

        @media (width >= 400px) {
            width: 200px;
        }

        @media (--medium) {
            width: 300px;
        }

        @media (--range-xsmall-down) {
            width: 100%;
        }

        span {
            text-align: start;

            @include line-clamp(1);
        }
    }

    .dropdown-button :global(.chevron),
    .dropdown-select-item :global(.chevron) {
        height: 12px;
        fill: var(--dropdownLightGrayIcon, $dropdown-light-gray-icon);
        transform: rotate(90deg);
        padding-inline-start: 12px;
        padding-inline-end: 12px;
    }

    ul {
        position: absolute;
        border-radius: $dropdown-menu-border-radius;
        border: 1px solid var(--dropdownLightGray, $dropdown-light-gray);
        background-color: var(--dropdownBackground, $dropdown-background);
        color: var(--systemPrimary-onDark);
        min-height: var(--selectHeight);
        user-select: none;
        width: 160px;
        z-index: var(--z-default);

        @media (width >= 400px) {
            width: 200px;
        }

        @media (--medium) {
            width: 300px;
        }

        .open-up & {
            bottom: -1px; // Compensate for outline border
        }

        .open-down & {
            top: -1px; // Compensate for outline border
        }
    }

    .dropdown-select-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid var(--dropdownLightGray, $dropdown-light-gray);
        font: var(--title-3);

        &:last-of-type {
            border-bottom: 0;
        }

        &:hover {
            background: var(--dropdownLightGray, $dropdown-light-gray);

            &:first-of-type {
                border-top-left-radius: calc(
                    $dropdown-menu-border-radius - 2px
                );
                border-top-right-radius: calc(
                    $dropdown-menu-border-radius - 2px
                );
            }

            &:last-of-type {
                border-bottom-left-radius: calc(
                    $dropdown-menu-border-radius - 2px
                );
                border-bottom-right-radius: calc(
                    $dropdown-menu-border-radius - 2px
                );
            }
        }

        span {
            line-height: var(--selectHeight);
            padding-inline-start: 30px;

            @include line-clamp;
        }
    }

    .selected::before {
        content: '✓';
        position: absolute;
        color: var(--dropdownLightGrayIcon, $dropdown-light-gray-icon);
        inset-inline-start: 9px;
        z-index: var(--z-default);
    }

    .select-list {
        grid-area: away-select;

        @media (--range-xsmall-down) {
            width: 100%;
        }
    }

    .select-button {
        min-width: 125px;
        height: var(--selectHeight);
        background-color: var(--keyColor);
        color: var(--systemPrimary-onDark);
        border-radius: $dropdown-menu-border-radius;
        grid-area: away-button;
        padding-inline-start: 30px;
        padding-inline-end: 30px;
        font: var(--body-emphasized);
    }
</style>
